import type { useI18n } from 'vue-i18n';
import type { NativeDateInput } from './dates';
import { Temporal } from 'temporal-polyfill';
import { translateString } from '#layers/i18n/public';
import { toNativeDate } from './dates';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_MINUTE_MS = 60 * 1000;
const relativeTimeFormatters = new Map<string, Intl.RelativeTimeFormat>();

/** Formatter construction is not free and these run per row in tables, so cache one formatter per locale. */
function getRelativeTimeFormat(i18n: ReturnType<typeof useI18n>): Intl.RelativeTimeFormat {
	const locale = i18n.locale.value;
	let formatter = relativeTimeFormatters.get(locale);

	if (!formatter) {
		formatter = new Intl.RelativeTimeFormat(locale, { style: 'long' });
		relativeTimeFormatters.set(locale, formatter);
	}

	return formatter;
}

function getCurrentLocalPlainDate(): Temporal.PlainDate {
	return plainDateFromLocalDate(new Date());
}

export interface FormatRelativeTimeOptions {
	granularity?: 'minute';
}

export function formatRelativeTime(date: NativeDateInput, i18n: ReturnType<typeof useI18n>, options?: FormatRelativeTimeOptions): string {
	const nativeDate = toNativeDate(date);

	if (options?.granularity !== 'minute') {
		return formatRelativePlainDate(plainDateFromLocalDate(nativeDate), i18n);
	}

	return formatRelativeEpochMilliseconds(nativeDate.getTime(), i18n);
}

export function formatRelativePlainDate(date: Temporal.PlainDate, i18n: ReturnType<typeof useI18n>): string {
	const today = getCurrentLocalPlainDate();
	const dayDifference = date.since(today, { largestUnit: 'days' }).days;

	if (dayDifference === 0) {
		return translateString('$t:time.today', i18n).toLocaleLowerCase();
	}

	return getRelativeTimeFormat(i18n).format(dayDifference, 'days');
}

export function formatRelativePlainDateTime(
	dateTime: Temporal.PlainDateTime,
	i18n: ReturnType<typeof useI18n>,
	options?: FormatRelativeTimeOptions,
): string {
	if (options?.granularity !== 'minute') {
		return formatRelativePlainDate(dateTime.toPlainDate(), i18n);
	}

	const instant = dateTime.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
	return formatRelativeTime(instant, i18n, options);
}

function formatRelativeEpochMilliseconds(epochMilliseconds: number, i18n: ReturnType<typeof useI18n>): string {
	const now = Date.now();
	const difference = epochMilliseconds - now;
	const absoluteDifference = Math.abs(difference);

	if (absoluteDifference < ONE_HOUR_MS) {
		const minuteDifference = Math.round(difference / ONE_MINUTE_MS);
		return minuteDifference === 0
			? translateString('$t:time.just_now', i18n).toLocaleLowerCase()
			: getRelativeTimeFormat(i18n).format(minuteDifference, 'minutes');
	}

	if (absoluteDifference < ONE_DAY_MS) {
		return getRelativeTimeFormat(i18n).format(Math.round(difference / ONE_HOUR_MS), 'hours');
	}

	return formatRelativePlainDate(plainDateFromLocalDate(new Date(epochMilliseconds)), i18n);
}

function plainDateFromLocalDate(date: Date): Temporal.PlainDate {
	return new Temporal.PlainDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}
